import { PureComponent } from "react";
import { UserDataAndState } from "common/Database";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import { isResponseOK, defined } from "common/Support";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { logError } from "common/debug-print";
import { redirectToStripeCheckout } from "webapp/lib/stripe-api";

export type Props = Readonly<{
    userData: UserDataAndState;
}>;

function closeWindow() {
    window.close();
}

interface StripeCheckoutParamsForTemplates {
    endpoint: "acquireStripeSessionForTemplatePurchase";
}
type StripeCheckoutParams = StripeCheckoutParamsForTemplates;

export abstract class PurchaseAppScreen<P extends Props = Props> extends PureComponent<P> {
    private targetAppID: string | undefined;
    private idempotencyKey: string | undefined;
    private handledAction = false;
    protected selfURL: URL;

    constructor(props: P) {
        super(props);

        const pathParts = window.location.pathname.split("/");
        this.selfURL = new URL(window.location.href);
        this.targetAppID = pathParts.length > 0 ? pathParts[pathParts.length - 1] : undefined;
        this.idempotencyKey = this.selfURL.searchParams.get("idempotencyKey") ?? undefined;
    }

    protected setHandled(): void {
        this.handledAction = true;
    }

    protected successParameterIsSet(): boolean {
        return this.selfURL.searchParams.get("success") !== null;
    }

    protected onBehalfOf(): string | undefined {
        return this.selfURL.searchParams.get("onBehalfOf") ?? undefined;
    }

    protected getTargetAppID(): string {
        return defined(this.targetAppID);
    }

    protected closeWindow(): void {
        closeWindow();
    }

    protected async performStripeRedirect(params: StripeCheckoutParams) {
        this.setHandled();
        const appFacilities = getAppFacilities();

        const successURL = new URL(window.location.href);
        successURL.searchParams.set("success", "");

        const cancelURL = new URL(window.location.href);
        cancelURL.searchParams.append("canceled", "");

        const { endpoint } = params;
        const body: any = {
            appID: this.targetAppID,
            paidURL: successURL.href,
            canceledURL: cancelURL.href,
            idempotencyKey: this.idempotencyKey,
        };

        const onBehalfOf = this.onBehalfOf();
        if (onBehalfOf !== undefined) {
            body.targetOwnerID = onBehalfOf;
        }

        const response = await appFacilities.callAuthCloudFunction(endpoint, body);

        if (!isResponseOK(response)) {
            logError(`Could not ${endpoint}:`, response?.statusText);
            closeWindow();
            return;
        }

        // We already have the purchase, we're fine.
        if (response.status === 204) return;

        const { sessionID, accountID } = await response.json();
        const error = await redirectToStripeCheckout(sessionID, accountID);
        // If we have made it this far, something has gone horribly wrong.
        // The above function is supposed to redirect us to Stripe Checkout,
        // which means we should be gone.
        logError(`Could not redirect to Stripe checkout:`, error);
        closeWindow();
    }

    protected abstract performStripeRedirectIfNecessary(): Promise<void>;

    private async performStripeRedirectIfNotHandled(): Promise<void> {
        if (this.handledAction) return;
        try {
            return await this.performStripeRedirectIfNecessary();
        } catch (e: unknown) {
            logError("Could not finish Stripe redirect", e);
            this.setHandled();
            this.closeWindow();
        }
    }

    public componentDidMount() {
        if (this.targetAppID === undefined) {
            this.handledAction = true;
            closeWindow();
            return;
        }

        const { location } = window;
        const params = new URLSearchParams(location.search);
        if (params.get("canceled") !== null) {
            this.handledAction = true;
            closeWindow();
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.performStripeRedirectIfNotHandled();
    }

    public componentDidUpdate() {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.performStripeRedirectIfNotHandled();
    }

    public render() {
        return <LoadingIndicator />;
    }
}
